import React, {useEffect, useState} from "react";
import ModalComponent from "components/ModalComponent";
import { Form, Row, Col } from 'react-bootstrap';
import {apiFetch} from "utils";
import Placeholder from 'components/Placeholder';
import requireScript from "utils/requireScript";
import FA from '../../../utils/FA';
import './styles/StripeBankAccount.scss';
import InfoTooltip from '../../shared/InfoTooltip'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

const loadStripe = () => {
  return requireScript('https://js.stripe.com/v3/', {
    unless: global.Stripe
  });
}

const getStripeToken = (customer, country, currency, accountNumber, routingNumber, name, type) => {
  return loadStripe().then(() => {
    const stripe = global.Stripe(customer.stripe_api_key);
    return stripe.createToken('bank_account', {
      country: country,
      currency: currency,
      routing_number: routingNumber,
      account_number: accountNumber,
      account_holder_name: name,
      account_holder_type: type || undefined
    });
  });
}

const StripeBankAccount = ({ customer, updateCustomer }) => {
  const paymentCountry  = customer.country;
  const paymentCurrency = customer.currency;

  const [error, setError]                   = useState('');
  const [submitted, setSubmitted]           = useState(false);
  const [submitting, setSubmitting]         = useState(false);
  const [alreadyCreated, setAlreadyCreated] = useState(false);
  const [isCreated, setIsCreated]           = useState(false);
  const [isLoading, setIsLoading]           = useState(true);
  const [specialCharacterFlag, setSpecialCharacterFlag] = useState(false)

  const [accountHolderName, setAccountHolderName] = useState("");
  const [accountHolderType, setAccountHolderType] = useState("company");
  const [accountNumber, setAccountNumber]         = useState("");
  const [routingNumber, setRoutingNumber]         = useState("");
  const [statementDescriptor, setStatementDescriptor] = useState(customer.ach_payment_info.statement_descriptor || '')
  const [defaultStatement, setDefaultStatement] = useState('')

  useEffect(() => {
    apiFetch.get({}, 'stripe_custom/external_accounts')
      .then(json => {
        if(json) {
          setAlreadyCreated(Boolean(json.id));
          setIsCreated(Boolean(json.id));

          setAccountHolderName(json.account_holder_name);
          setAccountHolderType(json.account_holder_type);
          setAccountNumber("****" + json.last4);
          setRoutingNumber(json.routing_number);
        }

      })
      .then(() => {
        apiFetch.get({}, 'stripe_custom/account')
          .then(json => {
            setDefaultStatement(json.settings.payments.statement_descriptor)
            setIsLoading(false);
          })
      })
  }, []);

  const handleSubmit = () => {
    setError('');
    setSubmitted(false);
    setSubmitting(true);
    const paymentConfig = {
      statement_descriptor: statementDescriptor
    }
    getStripeToken(customer, paymentCountry, paymentCurrency, accountNumber, routingNumber, accountHolderName, accountHolderType, statementDescriptor)
      .then((json) => {
        if(json.token) {
          return apiFetch.post({}, 'stripe_custom/external_accounts', {
            external_account: json.token.id
          })
        } else {
          return Promise.reject(json.error);
        }
      })
      .then(() =>{
        updateCustomer(customer, paymentConfig)
      }).then(_json => {
        setIsCreated(true);
        setSubmitting(false);
        setSubmitted(true);
      }).catch((e) => {
        console.error("Failed to submit stripe token", e);
        setError((e && e.message || e.error) || 'Something went wrong.');
        setSubmitting(false);
        setSubmitted(true);
      });
  }

  // todo: this code would be deleted when "Manage bank account" step 3 is removed
  const preventSpecialCharacters = event => {
    if (/[<>\\'"*]/.test(event.target.value)) {
      setSpecialCharacterFlag(true)
    }
    setStatementDescriptor(event.target.value.replace(/[<>\\'"*]/gi, ""))
  }

  const submitButton = <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || statementDescriptor.length > 19}>
    {submitting ? <React.Fragment>
      <FA name={'spinner'} spin nbsp/> Submitting...
    </React.Fragment> :
      (isCreated ? 'Update' : 'Submit')}
  </button>

  return <ModalComponent className='stripe-bank-account-modal' buttons={[submitButton]} title="Manage Bank Account">
    <Placeholder.loading ready={!isLoading}>
      <p><span style={{color: 'red'}}>*</span> indicates required fields</p>

      <Form>
        <Form.Group as={Row}>
          <Form.Label column sm={8}>
            Account Holder Name
            <span style={{color: "red"}}>*</span>
          </Form.Label>
          <Col sm={16}>
            <Form.Control
              aria-required="true"
              defaultValue={accountHolderName}
              name="accountName"
              onChange={(e) => setAccountHolderName(e.target.value)}
              type="text"
              size='sm'
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Form.Label column sm={8}>
            Account Holder Type
          </Form.Label>
          <Col sm={16}>
            <select
              className='form-select form-select-sm'
              aria-required="true"
              defaultValue={accountHolderType}
              name="accountType"
              onChange={(e) => setAccountHolderType(e.target.value)}
            >
              <option value=""></option>
              <option value="company">Company</option>
              <option value="individual">Individual</option>
            </select>
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Form.Label column sm={8}>
            Routing Number
            {paymentCountry === 'US' && <span style={{color: "red"}}>*</span>}
          </Form.Label>
          <Col sm={16}>
            <Form.Control
              aria-required="true"
              defaultValue={routingNumber}
              name="routingNumber"
              onChange={(e) => setRoutingNumber(e.target.value)}
              type="text"
              size='sm'
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Form.Label column sm={8}>
            Account Number
            <span style={{color: "red"}}>*</span>
          </Form.Label>
          <Col sm={16}>
            <Form.Control
              aria-required="true"
              defaultValue={accountNumber}
              name="accountNumber"
              onChange={(e) => setAccountNumber(e.target.value)}
              type="text"
              size='sm'
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Form.Label column sm={8}>
            Country
            <span style={{color: "red"}}>*</span>
          </Form.Label>
          <Col sm={16}>
            <Form.Control
              aria-required="true"
              defaultValue={paymentCountry}
              name="country"
              type="text"
              disabled
              size='sm'
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Form.Label column sm={8}>
            Currency
            <span style={{color: "red"}}>*</span>
          </Form.Label>
          <Col sm={16}>
            <Form.Control
              aria-required="true"
              defaultValue={paymentCurrency}
              name="currency"
              type="text"
              disabled
              size='sm'
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row}>
          <Form.Label column sm={8}>
            Statement Descriptors
            <InfoTooltip
              icon={faInfoCircle}
              nbsp>
              Define how billing charges from your school will appear on bank statements.  All descriptors will start with the prefix SA* and will be followed by up to 19 characters. Example: SA*YOUR SCHOOL NAME
            </InfoTooltip>
          </Form.Label>
          <Col sm={16}>
            <Form.Control
              aria-required="true"
              name="descriptor"
              value={statementDescriptor}
              type="text"
              size='sm'
              onChange={preventSpecialCharacters}
              isInvalid={statementDescriptor.length > 19}
            />
            <p className="medium-grey statement-disclaimer">Preview: SA*{statementDescriptor || defaultStatement}</p>
            {specialCharacterFlag ?
              <p className="red" style={{ fontSize: '0.875em', marginTop: '4px', marginBottom: '0'}}>
                Please do not use the following characters &lt;, &gt;, \, ', ", *.
              </p> :
              ''
            }
            <Form.Control.Feedback type="invalid" >
              Please limit your description to 19 characters or less.
            </Form.Control.Feedback>
          </Col>
        </Form.Group>
      </Form>

      {error && <p className='alert alert-danger'>{error}</p>}

      {submitted && !error &&
        <p>
          <b style={{color: 'green'}}>
            Bank account information has been {alreadyCreated ? "updated" : "saved"}! Click "Close" to proceed
          </b>
        </p>
      }
    </Placeholder.loading>
  </ModalComponent>;
}

export default StripeBankAccount;
